import {
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

export type ReportHeatmapSliceAxis = 'x' | 'y'

export interface ReportHeatmapSlice {
  axis: ReportHeatmapSliceAxis | undefined
  index: number
  bounds: GeckoComponentRect
}

interface ReportHeatmapSliceOptions {
  axis?: ReportHeatmapSliceAxis
  maxAspectRatio?: number
  maxSliceCount?: number
  sliceCount?: number
}

export interface ReportHeatmapImageSize {
  width: number
  height: number
}

export interface ReportHeatmapCompositeLayout {
  width: number
  height: number
  placements: Array<{ x: number; y: number }>
}

const DEFAULT_MAX_ASPECT_RATIO = 2.5
const DEFAULT_MAX_SLICE_COUNT = 3
const MIN_SLICE_LENGTH_METERS = GeckoMeasurement.CreateFromValue(
  20,
  GeckoMeasurementUnit.Feet
).toNumber(GeckoMeasurementUnit.Meters)

const getSliceCount = (
  aspectRatio: number,
  maxAspectRatio: number,
  maxSliceCount: number
) =>
  Math.min(maxSliceCount, Math.max(1, Math.ceil(aspectRatio / maxAspectRatio)))

const createSliceBounds = (
  bounds: GeckoComponentRect,
  axis: ReportHeatmapSliceAxis,
  index: number,
  sliceCount: number
) => {
  const xStart = bounds.x.min.num
  const xEnd = bounds.x.max.num
  const yStart = bounds.y.min.num
  const yEnd = bounds.y.max.num

  if (axis === 'x') {
    const sliceWidth = (xEnd - xStart) / sliceCount
    return GeckoComponentRect.CreateFromXYs(
      bounds.componentID,
      xStart + sliceWidth * index,
      yStart,
      xStart + sliceWidth * (index + 1),
      yEnd,
      bounds.x.unit,
      bounds.y.unit
    )
  }

  const sliceHeight = (yEnd - yStart) / sliceCount
  return GeckoComponentRect.CreateFromXYs(
    bounds.componentID,
    xStart,
    yStart + sliceHeight * index,
    xEnd,
    yStart + sliceHeight * (index + 1),
    bounds.x.unit,
    bounds.y.unit
  )
}

export const getReportHeatmapSlices = (
  bounds: GeckoComponentRect,
  options: ReportHeatmapSliceOptions = {}
): ReportHeatmapSlice[] => {
  // Angular X coordinates represent the circumference of a polar surface.
  // Splitting that axis would turn one radial heatmap into unrelated wedges,
  // so keep each polar section together.
  if (
    bounds.x.unit === GeckoMeasurementUnit.Degrees ||
    bounds.x.unit === GeckoMeasurementUnit.Radians
  ) {
    return [{ axis: undefined, index: 0, bounds }]
  }

  const maxAspectRatio = options.maxAspectRatio ?? DEFAULT_MAX_ASPECT_RATIO
  const maxSliceCount = options.maxSliceCount ?? DEFAULT_MAX_SLICE_COUNT
  const width =
    bounds.x.unit === GeckoMeasurementUnit.Tubes
      ? bounds.width.num
      : bounds.width.toNumber(GeckoMeasurementUnit.Meters)
  const height = bounds.height.toNumber(GeckoMeasurementUnit.Meters)

  if (width <= 0 || height <= 0) {
    return [{ axis: undefined, index: 0, bounds }]
  }

  const axis: ReportHeatmapSliceAxis =
    options.axis ?? (width >= height ? 'x' : 'y')
  const aspectRatio = Math.max(width, height) / Math.min(width, height)
  const desiredSliceCount = options.axis
    ? maxSliceCount
    : getSliceCount(aspectRatio, maxAspectRatio, maxSliceCount)
  const axisLength = axis === 'x' ? width : height
  const calculatedSliceCount =
    Array.from({ length: desiredSliceCount - 1 }, (_, index) => index + 2)
      .filter(
        (candidateSliceCount) =>
          axisLength / candidateSliceCount >= MIN_SLICE_LENGTH_METERS
      )
      .at(-1) ?? 1
  const sliceCount = options.sliceCount ?? calculatedSliceCount

  if (sliceCount === 1) {
    return [{ axis: undefined, index: 0, bounds }]
  }

  return Array.from({ length: sliceCount }, (_, index) => ({
    axis,
    index,
    bounds: createSliceBounds(bounds, axis, index, sliceCount),
  }))
}

export const getReportHeatmapCompositeLayout = (
  axis: ReportHeatmapSliceAxis,
  imageSizes: ReportHeatmapImageSize[],
  gap = 4
): ReportHeatmapCompositeLayout => {
  if (imageSizes.length === 0) {
    return { width: 0, height: 0, placements: [] }
  }

  if (axis === 'x') {
    const width = Math.max(...imageSizes.map((size) => size.width))
    let y = 0
    const placements = imageSizes.map((size) => {
      const placement = { x: 0, y }
      y += size.height + gap
      return placement
    })

    return {
      width,
      height: y - gap,
      placements,
    }
  }

  const height = Math.max(...imageSizes.map((size) => size.height))
  let x = 0
  const placements = imageSizes.map((size) => {
    const placement = { x, y: 0 }
    x += size.width + gap
    return placement
  })

  return {
    width: x - gap,
    height,
    placements,
  }
}

const imageBitmapFromBlob = async (blob: Blob) => createImageBitmap(blob)

export const composeReportHeatmapBlobs = async (
  axis: ReportHeatmapSliceAxis,
  blobs: Blob[],
  gap = 4
): Promise<Blob> => {
  const bitmaps = await Promise.all(blobs.map(imageBitmapFromBlob))
  const layout = getReportHeatmapCompositeLayout(
    axis,
    bitmaps.map((bitmap) => ({ width: bitmap.width, height: bitmap.height })),
    gap
  )

  if (layout.width === 0 || layout.height === 0) {
    bitmaps.forEach((bitmap) => bitmap.close())
    throw new Error('Cannot compose an empty report heatmap')
  }

  const canvas = document.createElement('canvas')
  canvas.width = layout.width
  canvas.height = layout.height
  const context = canvas.getContext('2d')

  if (!context) {
    bitmaps.forEach((bitmap) => bitmap.close())
    throw new Error('Failed to create report heatmap composition context')
  }

  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  bitmaps.forEach((bitmap, index) => {
    const placement = layout.placements[index]
    context.drawImage(bitmap, placement.x, placement.y)
    bitmap.close()
  })

  const compositeBlob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/png')
  )

  if (!compositeBlob) {
    throw new Error('Failed to encode report heatmap composition')
  }

  return compositeBlob
}
